import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useBrandForm } from "../hooks/useBrandForm";
import { BrandDetailsForm } from "./BrandDetailsForm";
import {
  fetchBrands,
  submitBrandDetailsFromOnboarding,
} from "../../../services/brandService";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { debug } from "../../../utils/debugLogger";
import {
  mapBrandResponseToFormData,
  mapFormDataToBrandPayload,
} from "../utils/brandMapper";

interface BrandFormV2Props {
  customer_id: string;
  brandId?: string | null;
  onComplete?: () => void;
}

export const BrandFormV2: React.FC<BrandFormV2Props> = ({
  customer_id,
  brandId,
  onComplete,
}) => {
  const { idToken } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [existingBrandId, setExistingBrandId] = useState<string | null>(
    brandId || null
  );

  // Detect reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const {
    formData,
    errors,
    hasChanges,
    updateField,
    validateForm,
    setFormData,
  } = useBrandForm();

  // Fetch existing brand data if editing
  useEffect(() => {
    const fetchBrandData = async () => {
      if (!idToken || !customer_id) return;

      try {
        setIsLoading(true);
        setSaveError(null);
        debug.log("🔍 Fetching brands for customer:", customer_id);
        const response = await fetchBrands(customer_id, idToken);
        debug.log("📦 Brands response:", response);

        // Find the brand - either by brandId param or use the first (and should be only) brand for this customer
        let brand;
        if (brandId) {
          brand = response?.brands?.find((b: any) => b.brand_id === brandId);
        } else {
          // If no brandId provided, use the first existing brand for this customer (one brand per customer)
          brand = response?.brands?.[0];
          if (brand) {
            debug.log(
              "📌 Found existing brand for customer, will update instead of create"
            );
          }
        }

        debug.log("🎯 Found brand:", brand);

        if (brand) {
          // Store the brand_id for updating later
          const actualBrandId = brand.brand_id;
          debug.log("📌 Using brand_id for updates:", actualBrandId);
          setExistingBrandId(actualBrandId);

          // Use mapper utility to transform backend response to form data
          const formDataToSet = mapBrandResponseToFormData(brand);
          debug.log("📝 Setting form data:", formDataToSet);
          setFormData(formDataToSet);
        }
      } catch (error) {
        debug.error("Error fetching brand data:", error);
        setSaveError("Failed to load brand data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrandData();
  }, [customer_id, idToken, setFormData]); // Removed brandId from dependencies

  const handleSave = async () => {
    // Validate form
    if (!validateForm()) {
      setSaveError("Please fix the errors before saving");
      return;
    }

    if (!idToken) {
      setSaveError("Authentication error. Please try again.");
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      // Use mapper utility to transform form data to backend payload format
      const brandData = mapFormDataToBrandPayload(formData);

      await submitBrandDetailsFromOnboarding(
        customer_id,
        brandData,
        idToken,
        existingBrandId // Use the existing brand_id if found
      );

      debug.log("✅ Brand saved successfully to backend:", {
        customer_id,
        brandId: existingBrandId || "new",
        isUpdate: !!existingBrandId,
        fields: {
          brandName: formData.brandName,
          website: formData.website,
          phoneNumber: formData.phoneNumber,
          serviceArea: formData.serviceArea,
          servicesOffered: formData.servicesOffered,
          tagline: formData.tagline,
        },
      });

      setSaveSuccess(true);

      // Show success message briefly then redirect
      setTimeout(() => {
        if (onComplete) {
          onComplete();
        } else {
          navigate("/customer", {
            state: {
              showSuccess: true,
              message: brandId
                ? "Brand details updated successfully!"
                : "Brand created successfully!",
            },
          });
        }
      }, 1000);
    } catch (error) {
      debug.error("Error saving brand details:", error);
      setSaveError("Failed to save brand details. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] w-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-zinc-200 border-t-brand-primary-light" />
          <p className="text-sm text-zinc-600">Loading brand details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-transparent">
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
      <motion.div
        className="w-full overflow-y-auto"
        initial={prefersReducedMotion ? {} : { x: -12, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0 : 0.35,
          ease: "easeOut",
        }}
      >
        <div className="mx-auto max-w-3xl px-1 py-2 sm:px-0 sm:py-0">
            <BrandDetailsForm
              formData={formData}
              errors={errors}
              onFieldChange={updateField}
              customerId={customer_id}
            />

            {/* Save Button */}
            <div className="mt-8 border-t border-zinc-200/90 pt-6">
              {saveError && (
                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <p className="text-sm text-red-700">{saveError}</p>
                </motion.div>
              )}

              {saveSuccess && (
                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <p className="text-sm text-green-700">
                    {existingBrandId
                      ? "Brand updated successfully!"
                      : "Brand created successfully!"}
                  </p>
                </motion.div>
              )}

              <motion.button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                whileHover={
                  !isSaving && hasChanges ? { scale: 1.02 } : undefined
                }
                whileTap={!isSaving && hasChanges ? { scale: 0.98 } : undefined}
                animate={
                  hasChanges && !isSaving
                    ? { scale: [1, 1.02, 1] }
                    : { scale: 1 }
                }
                transition={{
                  scale: {
                    repeat: hasChanges && !isSaving ? Infinity : 0,
                    duration: 2,
                    ease: "easeInOut",
                  },
                }}
                className={`w-full rounded-lg px-6 py-2.5 text-sm font-semibold text-white transition-colors ${
                  isSaving || !hasChanges
                    ? "cursor-not-allowed bg-zinc-400"
                    : "bg-brand-primary hover:bg-brand-primary-hover"
                }`}
              >
                {isSaving ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.span
                      className="rounded-full h-4 w-4 border-b-2 border-white inline-block"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </motion.button>

              {!hasChanges && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mt-2 text-center text-xs text-zinc-500"
                >
                  No changes to save
                </motion.p>
              )}
            </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BrandFormV2;
